import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { FilterOptionsState } from "@mui/material";
import { AutocompleteChangeReason, AutocompleteCloseReason, AutocompleteProps, AutocompleteRenderGetTagProps, AutocompleteRenderGroupParams, createFilterOptions } from "@mui/material/Autocomplete";
import Popper, { PopperProps } from "@mui/material/Popper";
import TextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import DataStoreLoader from "csi/datastore/DataStoreLoader";
import { IDataStore } from "csi/typings";
import { useSyncLocalStore } from "designSystem/hooks";
import { FlexRow } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { largeListThreshold } from "designSystem/settings";
import { black, green, grey, red, white } from "designSystem/styles/color";
import { textFieldInnerOutlineStyle, textfieldStyle } from "designSystem/styles/style-functions";
import { AFTER, FONT_BOLD, FONT_SEMIBOLD } from "designSystem/theme";
import { ComponentItem } from "designSystem/types/component-item-types";
import { ALL_COMPONENT_ID, CSI_DROPDOWN_PLACEHOLDER, NONE_COMPONENT_ID, NO_MATCHES_COMPONENT_ID, componentItemGroupByDefaultFn, componentItemHasValues, componentItemIsEmpty, getAllComponentItem, getComponentItemDisabled, getNoMatchesComponentItem, isComponentItemEqualToValue } from "designSystem/utilities/component-item-functions";
import _ from "lodash";
import React, { HTMLAttributes, KeyboardEventHandler, ReactElement, ReactNode, SyntheticEvent, useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import Box from "../box/Box";
import { ButtonProps } from "../button/Button";
import Checkbox from "../checkbox/Checkbox";
import Chip from "../chip/Chip";
import FlexColumn from "../flex/FlexColumn";
import FlexRowCenter from "../flex/FlexRowCenter";
import CsiImage, { CsiImageProps } from "../image/CsiImage";
import OverflowText from "../text/OverflowText";
import Text from "../text/Text";
import Autocomplete, { POPPER_MAX_WIDTH } from "./AutoCompleteList";
import ReactWindowListboxComponent, { ReactWindowListboxComponentProps } from "./ReactWindowListboxComponent";
import { generatePinyinCode } from "./SelectHelper";
import { IconButtonProps } from "@mui/material/IconButton";

const PREFIX = "MultiSelect";

const classes = {
	addIcon: `${PREFIX}addIcon`,
	customItem: `${PREFIX}customItem`,
	selectedNames: `${PREFIX}selectedNames`,
	lastSelectedName: `${PREFIX}lastSelectedName`,
	multiSelectInput: `${PREFIX}multiSelectInput`,
	optionCheckbox: `${PREFIX}optionCheckbox`,
	optionCheckboxHidden: `${PREFIX}optionCheckboxHidden`,
	optionGroupSuggested: `${PREFIX}optionGroupSuggested`,
	optionInvalid: `${PREFIX}optionInvalid`,
	input: `${PREFIX}input`,
	inputComponent: `${PREFIX}inputComponent`,
	inputComponentWithSelection: `${PREFIX}inputComponentWithSelection`,
	innerBorderOutline: `${PREFIX}innerBorderOutline`,
};

interface MultiSelectIconButtonProps extends IconButtonProps {
	/** Allow for putting data-csi-automation tag on the icon button */
	"data-csi-automation"?: string;
}

const StyledAutocomplete = styled(Autocomplete<ComponentItem, boolean, boolean, boolean>)(({
	theme,
}) => ({
	[`& .${classes.addIcon}`]: {
		paddingLeft: 2,
	},

	[`& .${classes.customItem}`]: {
		fontStyle: "italic",
	},

	[`& .${classes.selectedNames}`]: {
		marginRight: 6,
		[AFTER]: {
			content: "\",\"",
		},
	},

	[`& .${classes.lastSelectedName}`]: {
		marginRight: 6,
		[AFTER]: {
			content: "\"   \"",
		},
	},

	[`& .${classes.multiSelectInput}`]: {
		"& $input:not(:focus):not(:placeholder-shown)": {
			maxWidth: 0,
			minWidth: 0,
			padding: 0,
			position: "absolute",
		},
	},

	// this remove buggy MUI warning "Could not find the referenced rule "input" in "makeStyles"."
	// https://github.com/mui-org/material-ui/issues/8048
	[`& .${classes.input}`]: {},

	[`& .${classes.inputComponent}`]: textfieldStyle(theme),
	[`& .${classes.inputComponentWithSelection}`]: textfieldStyle(theme, true),
	[`& .${classes.innerBorderOutline}`]: textFieldInnerOutlineStyle(theme),
}));

const StyledPopper = styled(Popper)(({
	theme,
}) => ({
	["& .MuiAutocomplete-noOptions"]: {
		display: "none",
	},
}));

const OptionGeneral = styled("ul")(({
	theme,
}) => ({
	paddingLeft: 0,
}));

const StyledOption = styled("li")(({
	theme,
}) => ({
	[`& .${classes.optionCheckbox}`]: {
		flexShrink: 0,
	},
	[`& .${classes.optionCheckboxHidden}`]: {
		visibility: "hidden",
		width: "2px !important",
	},
	[`& .${classes.optionInvalid}`]: {
		color: red[600],
		display: "inline-block",
		flexGrow: 1,
		flexShrink: 0,
		fontStyle: "italic",
		textAlign: "right",
	},
}));

const StyledPlaceholder = styled("li")(() => ({
	marginTop: -4,
	marginBottom: 4,
}));

const OptionGroupLabel = styled("div")(({
	theme,
}) => ({
	alignItems: "center",
	backgroundColor: white[50],
	display: "flex",
	height: 26,
	lineHeight: 26,
	paddingLeft: 8,
	position: "sticky",
	top: -4,
	zIndex: 1,

	"& :first-of-type": {
		fontWeight: FONT_BOLD,
	},
	[`& .${classes.optionGroupSuggested}`]: {
		backgroundColor: green[100],
		borderRadius: 3,
		color: green[700],
		display: "inline-block",
		fontWeight: FONT_SEMIBOLD,
		height: 18,
		lineHeight: "9px",
		marginLeft: 6,
		padding: 4,
	},
}));

/**
 * Option noChips disables using chips and turn multiselect into standard style checked multiselect control
 * Option noChips can be used together with option "limitTags". In this case, chips still not shown, if 
 * limitTags=N then label will show first N selected names and then show "+n" if selection is greater then limitTags
 * Option noChips enables showing "N Items selected" placeholder if limnitTags is not used.
 * Option noChips enables showing "Select" placeholder (if no selection made)
 */

export interface MultiSelectProps<T> extends Omit<Partial<AutocompleteProps<T, boolean, boolean, boolean>>, "onChange" | "onClose" | "value"> {
	// TO DO - use Virtualization(based on react-window - help address some common performance bottlenecks) /Zongfei
	/** Allow custom options - false by default */
	allowCustomOptions?: boolean;

	/** indicate whether or not enable Pinyin code, false by default */
	allowPinyin?: boolean;

	/** auto focus input */
	autoFocus?: boolean;

	/** data-csi-automation label for component */
	dataCsiAutomation?: string;

	/** delayed request made search and has results */
	delayedRequestHasResults?: boolean;

	/** drop down placeholder */
	DropdownPlaceholder?: ReactNode;

	/** number of items to be loaded, the rest of items can be searched, 200 is default */
	filterLimit?: number;

	/** Group the options by option.group - false by default */
	grouped?: boolean;

	/** Hide the "No options" popper when there are no results to display in the dropdown */
	hideNoResults?: boolean;

	/** image options to display  */
	ImageProps?: Omit<CsiImageProps, "src">;

	/** label for this component */
	label?: string;

	/** localization resource to use to lookup text; can be simple resource or type resource (i.e. "User.Name") */
	labelRes?: string;

	/** parameters to pass if looking up resource */
	labelResParams?: Array<number | string>;

	/** remove all chips */
	noChips?: boolean;

	/** prevent autocomplete input toggle by mouse and keyboard clicks (useful for inline editing)*/
	noInputToggle?: boolean;

	/** editing cancelled */
	onCancel?: () => void;

	/** callback for value change and drop-down close */
	onChange?: (event: React.ChangeEvent<{}>, values: ComponentItem[], reason: AutocompleteChangeReason) => void;

	/** callback to call if new custom value is detected */
	onCustomOption?: (newValue: ComponentItem) => void;

	/** callback on dropdown open */
	onOpenCallback?: () => Promise<void>;

	/** callback for every value change */
	onValueChange?: (event: React.ChangeEvent<{}>, values: ComponentItem[], reason: AutocompleteChangeReason) => void;

	/** options for the drop down, if "store" is defined, then this attribute will be ignored */
	options?: T[];

	/** place holder for the TextField component */
	placeHolder?: string;

	/** localization resource to use to lookup text; can be simple resource or type resource (i.e. "User.Name") */
	placeHolderRes?: string;

	/** parameters to pass if looking up resource */
	placeHolderResParams?: Array<number | string>;

	/** Indicates whether to set the width of popper component to fit the content */
	popperFitContentWidth?: boolean;

	/** indicates whether selecting None will remove selected items that were disabled */
	/** If this property is true, the items that are already selected and disabled will not be removed when you select the None option. Only the items selected in the current operation will be removed. */
	removeDisabledSelectionsOnNone?: boolean;

	/** Select Actions, which are displayed and stickied at the bottom of the list */
	selectActions?: ReactElement<ButtonProps>[];

	/** add "Select all" item; this option should not be used together with SELECT NONE	*/
	selectAll?: boolean;

	/** move selected items to top of list (only if not grouped) */
	selectedFirst?: boolean;

	/* indicate slim card rendering, attribute "name2" will be used for second attribute rendering  */
	slimCard?: boolean;

	/** reference to store containing item */
	store?: IDataStore<T>;

	/** groups to mark as suggested */
	suggestedGroups?: string[];

	/** value(s) */
	value?: T[];

}

function MultiSelect<T extends ComponentItem>(props: MultiSelectProps<T>) {
	const {
		allowCustomOptions = false,
		allowPinyin = false,
		autoFocus = false,
		clearOnBlur,
		dataCsiAutomation,
		delayedRequestHasResults,
		DropdownPlaceholder,
		selectActions = null,
		filterLimit,
		grouped = false,
		hideNoResults = false,
		inputValue,
		ImageProps,
		label,
		labelRes,
		labelResParams,
		ListboxComponent,
		noChips,
		noInputToggle,
		onBlur,
		onCancel,
		onChange,
		onValueChange,
		onCustomOption,
		onOpenCallback,
		open,
		options,
		placeHolder,
		placeHolderRes,
		placeHolderResParams,
		popperFitContentWidth,
		removeDisabledSelectionsOnNone,
		selectAll,
		selectedFirst,
		slimCard,
		store,
		suggestedGroups,
		value,
		...otherProps
	} = props;

	const {
		addIcon,
		multiSelectInput,
		customItem,
		innerBorderOutline,
		inputComponent,
		inputComponentWithSelection,
		lastSelectedName,
		optionCheckbox,
		optionCheckboxHidden,
		optionGroupSuggested,
		optionInvalid,
		selectedNames,
	} = classes;

	const loc = useLocale();
	const propLabel = label || (labelRes && loc.resource(labelRes, labelResParams));
	const { getComponentItemName, getComponentItemSlimName, resource } = loc;
	const itemNone = getNoMatchesComponentItem();

	// we start using hook value only after user change selection, until then ignore hooked value.
	// this is required to allow multiselect to change both options and value without triggering
	// "invalid value" mui autocomplete issue
	const [values, setValues] = useState<ComponentItem[]>([]);
	useEffect(() => {
		setValues(_.isEmpty(value) ? [] : value);
		selectAllCheckedRef.current = selectAll && !value?.length;
	}, [value]);

	// C8-55618: MUI autocomplete clear filter when user do new selection, which contradict
	// our usual behavior, so we remember filter text in filterTextRef and update the ref
	// on any filter change. We also force the code to use it when filtering
	const filterTextRef = useRef("");

	const [bUpdated, setUpdated] = useState<boolean>(false);
	const [showOptionsList, setShowOptionsList] = useState<boolean>(open || false);
	const pinyinIsGenerated = useRef<boolean>(false);

	// open will control component initial open state. If open is not specified, default component behavior will be controlled by showOptionlist
	const showOptions = open ?? showOptionsList;
	const getOptionLabel = slimCard ? getComponentItemSlimName : getComponentItemName;
	const loader = useSyncLocalStore(() => new DataStoreLoader<T>(store, 1, Infinity));
	let opts: T[] = [];

	// create options from store
	if (store) {
		const {
			error,
			hasResults,
			resultsWithIds,
		} = loader;

		if (hasResults && resultsWithIds.length && !error) {
			for (const [item, id] of resultsWithIds) {
				opts.push(item);
			}
		}
	} else {
		opts = [].concat(options);
	}

	// move selected options on top
	if (!grouped && selectedFirst && value) {
		const valueHash = Object.fromEntries(value.map((val, idx) => [val.id, idx]));
		opts.sort((option1, option2) => {
			if (option1.id === CSI_DROPDOWN_PLACEHOLDER) return -1;
			if (option2.id === CSI_DROPDOWN_PLACEHOLDER) return 1;
			if (option1.id === NONE_COMPONENT_ID) return -1;
			if (option2.id === NONE_COMPONENT_ID) return 1;
			return ((valueHash[option1.id] >= 0) && valueHash[option2.id] === undefined) ? -1 : 0;
		});
	}

	// select all state
	const selectAllCheckedRef = useRef(false);

	// use React Window library (ListboxComponent) to render large lists
	const isLargeList = opts.length > largeListThreshold;

	let text = "";
	const selectedValuesExist = componentItemHasValues(values);
	const numSelected = selectedValuesExist ? values.length : 0;
	const tagsLimit = props.limitTags;

	// if "noChips" - simulate old style placeholder text for single or multiple selection
	if ((!tagsLimit || showOptionsList) && noChips && numSelected) {
		if (numSelected == 1) {
			text = getOptionLabel(values[0]);
		} else {
			text = resource("ItemsSelected", [numSelected]);
		}
	}

	// show placeholder text if placeHolderRes is specified and not "noChips"
	if (!tagsLimit || !noChips || (numSelected == 0)) {
		text = text || (placeHolderRes ? resource(placeHolderRes, placeHolderResParams || [numSelected]) : null);
	}

	if (!text && selectAllCheckedRef.current) {
		text = resource("ItemsSelectedAll", [opts.filter((item) => (item.id !== NONE_COMPONENT_ID) && (item.id !== ALL_COMPONENT_ID) && !item.disabled).length]);
	}

	const addDelayedSearchPlaceholder = (options: ComponentItem[]) => {
		options.unshift({
			id: CSI_DROPDOWN_PLACEHOLDER,
			disabled: true,
		});
	};

	// handler for filter options
	const filter = useCallback(createFilterOptions<ComponentItem>({
		limit: filterLimit || 5000,
		stringify: allowPinyin ? (option) => option.pinyinCode : null,
	}), [filterLimit]);

	function slimCardFilterOptions<T extends ComponentItem>(options: T[], params: FilterOptionsState<T>): ComponentItem[] {
		const { inputValue } = params;
		const inputValueRegExp = new RegExp(inputValue, "i");
		return options.filter((item) => getComponentItemName(item)
			?.match(inputValueRegExp) || item.name2?.match(inputValueRegExp));
	}

	// Handle the custom options
	function filterOptions<T extends ComponentItem>(options: T[], params: FilterOptionsState<T>): ComponentItem[] {
		if (allowPinyin && !pinyinIsGenerated.current) {
			generatePinyinCode<T>(options);
			pinyinIsGenerated.current = true;
		}

		params.inputValue = filterTextRef.current;
		const filtered = slimCard ? slimCardFilterOptions(options, params) : filter(options, params);

		// preserve option "none"
		let hasNone = false;
		for (let i = 0; (i < 2) && (options.length >= i + 1) && !hasNone; i++) {
			if (filtered[i]?.id === NONE_COMPONENT_ID) {
				hasNone = true;
			}
		}
		for (let i = 0; !hasNone && (i < 2) && (options.length >= i + 1); i++) {
			if (options[i]?.id === NONE_COMPONENT_ID) {
				hasNone = true;
				filtered.unshift(options[i]);
			}
		}
		// add "no results" item if there are "select none" and original options list is not empty but filtered list is empty
		// if there are no "Select None" item then autocomplete list will add "no results" item
		if (options.length && hasNone && filtered.length === 1) {
			filtered.push(itemNone);
		}

		// always add the custom one to the top
		if (allowCustomOptions) {
			const { inputValue } = params;
			if ((inputValue !== "") && (!filtered.some((item) => item.id === inputValue))) {
				filtered.unshift({
					customOption: true,
					id: inputValue,
					name: inputValue,
					value: inputValue,
				});
			}
		}

		if (DropdownPlaceholder) {
			addDelayedSearchPlaceholder(filtered);
		}

		// Add "Select All" item
		if (options.length && selectAll && !filterTextRef.current) {
			filtered.unshift(getAllComponentItem(loc));
		}

		return filtered;
	}

	useEffect(() => {
		// ensure that loader is updated with the latest store
		loader.setStore(store);
	}, [store]);

	// update the open state
	function onOpenDropdown(event: SyntheticEvent<Element, Event>): void {
		onOpenCallback?.();
		setShowOptionsList(true);
	};

	// update the selected values and callback as needed
	function onChangeValue(event: SyntheticEvent<Element, Event>, newValues: ComponentItem[], reason: AutocompleteChangeReason, details: { option: ComponentItem }): void {
		// Neither the "clear items" button, the ESC key nor the backspace key
		// respect an item's disabled attribute.
		// So, if any previously selected disabled item was removed, we must put it back.

		let code;
		if ("code" in event) {
			code = event.code;
		}

		const newOption = details?.option;

		// C8-56121: do not unselect option when clicking "backspace" key with empty filter
		if (
			noChips &&
			(reason === "removeOption") &&
			(event.type === "keydown") &&
			(code === "Backspace")
		) {
			// restore unselected value
			newValues.push(newOption);
			return;
		}

		const noLongerPresentList = _.differenceBy(values, newValues, "id");
		newValues = newValues.concat(noLongerPresentList.filter((v) => v.disabled));

		let newValueItems = newValues as ComponentItem[];

		if (newValues.some((item) => componentItemIsEmpty(item) && selectedValuesExist)) {
			if (removeDisabledSelectionsOnNone) {
				newValueItems = [];
			} else {
				newValueItems = newValueItems.filter((option) => !componentItemIsEmpty(option) && option.disabled);
			}
		} else if (details) {
			const { option } = details;
			if (allowCustomOptions && onCustomOption && option.customOption && !opts.some((item) => item.id === option.id)) {
				onCustomOption(option);
			}
			// we exclude the none item since it symbolizes that no item is selected and thus none is present
			newValueItems = newValueItems.filter((option) => !componentItemIsEmpty(option));
		}

		// select/unselect all
		if (selectAll) {
			// as we never add "Select All" to value, clicking on it will always cause "selectOption" and never "removeOption"
			if ((reason === "selectOption") && (newOption?.id === ALL_COMPONENT_ID)) {
				selectAllCheckedRef.current = true;

				// preserve disabled values selection
				newValueItems = newValues?.filter((item) => (item.id !== NONE_COMPONENT_ID) && (item.id !== ALL_COMPONENT_ID) && item.disabled) || [];
			}
			// uncheck "Select All" if one of other options selection changed
			if (newOption?.id !== ALL_COMPONENT_ID) {
				selectAllCheckedRef.current = !newValueItems.length;
			}
		}

		setUpdated(true);
		setValues(newValueItems);

		onValueChange?.(event, newValueItems, reason);

		// callback once values are getting clear out or deleted, but only happens when drop-down is closed
		if (!showOptions && ("clear" === reason || "removeOption" === reason)) {
			onChange?.(event, newValueItems, reason);
		}
	};

	// callback when closing drop-down
	function onCloseDropdown(event: SyntheticEvent<Element, Event>, reason: AutocompleteCloseReason): void {
		// don't close dropdown when click on input control
		if (noInputToggle && (reason === "toggleInput") && ((event.target as Element).tagName === "INPUT")) {
			return;
		}
		if (bUpdated) {
			onChange?.(event, values, reason as AutocompleteChangeReason);
		} else {
			onCancel?.();
		}

		// reset the status
		setUpdated(false);

		setShowOptionsList(false);
	}


	// process keyboard input
	const onKeyDown: KeyboardEventHandler = (ev) => {
		const { code, ctrlKey, metaKey } = ev;
		// select all
		if ((code === "KeyA") && (ctrlKey || metaKey)) {
			let newValue = _.cloneDeep(opts)
				.filter((item) => (item.id !== NONE_COMPONENT_ID) && (item.id !== ALL_COMPONENT_ID) && !item.disabled);

			// preserve disabled values selection
			const oldDisabledValue = value?.filter((item) => (item.id !== NONE_COMPONENT_ID) && (item.id !== ALL_COMPONENT_ID) && item.disabled) || [];
			newValue = newValue.concat(oldDisabledValue);
			selectAllCheckedRef.current = false;

			setValues(newValue);
			setUpdated(true);
			onValueChange?.(ev, newValue, "selectOption");
		}
	};

	const groupBy = grouped ? componentItemGroupByDefaultFn : null;

	function renderInput({ InputProps, ...params }): ReactNode {
		const [, forceUpdateInput] = useReducer((x) => x + 1, 0);
		params.inputProps.value = filterTextRef.current;
		return (
			<TextField
				{...params}
				InputProps={{
					classes: {
						notchedOutline: innerBorderOutline,
						root: (noChips && numSelected) ? inputComponentWithSelection : inputComponent,
					},
					...InputProps,
				}}
				autoFocus={autoFocus}
				className={multiSelectInput}
				data-csi-automation={`dropdown-${dataCsiAutomation}`}
				label={propLabel}
				onChange={(e) => {
					const filterText = e.target.value;
					filterTextRef.current = filterText;
					// C8-56313: if user deleted filter text - help component to rerender the input
					if (filterText === "") {
						forceUpdateInput();
					}
				}}
				onKeyDown={onKeyDown}
				placeholder={text || placeHolder}
			/>
		);
	}

	function renderOption<T extends ComponentItem>(props: HTMLAttributes<HTMLLIElement>, option: T, { selected }): ReactNode {
		const { className, ...otherProps } = props;
		const { customOption, id, invalid, isEmptyGroupItem, name2, imageSrc } = option;
		const isOptionNone = id === NONE_COMPONENT_ID;
		const isNoMatches = id === NO_MATCHES_COMPONENT_ID;
		const isSelectAll = id === ALL_COMPONENT_ID;
		const isSlimCard = slimCard && !isOptionNone && !isEmptyGroupItem;
		const optionClassName = isOptionNone ?
			clsx(className, "autocomplete-option-none", !selectedValuesExist && "autocomplete-option-none-highlight") :
			isEmptyGroupItem ? className :
			isSelectAll ? clsx(className, "autocomplete-option-select-all") :
			clsx(className, "autocomplete-option-multiselect", slimCard && "autocomplete-option-slim");
		const isChecked = (isSelectAll && selectAllCheckedRef.current) || (isOptionNone && !selectedValuesExist || !isOptionNone && selected);
		const optionAutomation = dataCsiAutomation ? "dropdown-" + dataCsiAutomation + "-Option" + props["data-option-index"] : undefined;

		if (id === CSI_DROPDOWN_PLACEHOLDER) {
			return (
				<StyledPlaceholder key={id}>
					{DropdownPlaceholder}
				</StyledPlaceholder>
			);
		}
		return (
			<StyledOption
				className={optionClassName}
				data-csi-automation={optionAutomation}
				data-csi-value={option.value}
				{...otherProps}
				key={id}
			>
				<FlexRowCenter gap={1} width={1}>
					<Checkbox
						checked={isChecked}
						className={clsx(optionCheckbox, ((isEmptyGroupItem || isNoMatches) && optionCheckboxHidden))}
						invalid={invalid}
					/>
					{
						ImageProps && (
							<Box
								border={1}
								borderColor={grey[400]}
								borderRadius={1}
								flexShrink={0}
								height={32}
								width={32}
							>
								<CsiImage
									src={imageSrc}
									{...ImageProps}
								/>
							</Box>
						)
					}
					<FlexColumn width="100%">
						<FlexRow>
							<OverflowText
								className={customOption ? customItem : null}
								color={black[100]}
								text={getComponentItemName(option)}
								tooltipProps={{ placement: "right" }}
							/>
							{customOption &&
								<AddIcon className={addIcon} />
							}
							{invalid && (
								<Text
									className={optionInvalid}
									res="Invalid"
								/>
							)}
						</FlexRow>
						{isSlimCard && name2 && (
							<OverflowText text={name2} />
						)}
					</FlexColumn>
				</FlexRowCenter>
			</StyledOption>
		);
	}

	function renderGroup(params: AutocompleteRenderGroupParams): ReactNode {
		let { children } = params;
		if (!Array.isArray(children)) {
			children = null;
		}
		const attributes: any = {};
		attributes.key = params.key;
		if (isLargeList && children) {
			attributes["data-csi-child"] = children;
		}
		return (
			<li {...attributes}>
				{!!params.group && (
					<OptionGroupLabel>
						<OverflowText text={params.group} />
						{suggestedGroups?.includes(params.group) &&
							<Text className={optionGroupSuggested} res="SuggestedUpperCase" />
						}
					</OptionGroupLabel>
				)}
				<OptionGeneral className="MuiAutocomplete-groupUl">
					{params.children}
				</OptionGeneral>
			</li>
		);
	}

	function renderTags<T extends ComponentItem[]>(tagOptions: T, getTagProps: AutocompleteRenderGetTagProps): ReactNode {
		return tagOptions.map((tagOption: ComponentItem, index: number) => {
			const { customOption, disabled } = tagOption;
			const localizedName = getOptionLabel(tagOption);

			if (componentItemIsEmpty(tagOption)) {
				return null;
			} else if (noChips) {
				return (
					tagsLimit && !showOptionsList && (
						<OverflowText
							className={index < (Math.min(tagsLimit, numSelected) - 1) ? selectedNames : lastSelectedName}
							key={getTagProps({ index })["key"]}
							text={localizedName}
						/>
					)
				);
			} else {
				return (
					<Chip
						classes={{ label: customOption ? customItem : null }}
						clickable={false}
						disabled={disabled}
						icon={customOption ? (<EditIcon />) : null}
						label={localizedName}
						{...getTagProps({ index })}
						onDelete={disabled ? null : getTagProps({ index })["onDelete"]}
					/>
				);
			}
		});
	}

	// autocomplete clear input value on blur by default, so we have to clear ref value as well
	const _onBlur = (ev) => {
		if (clearOnBlur !== false) {
			filterTextRef.current = "";
		}
		onBlur?.(ev);
		// C8-62452: call "onCancel" if onBlur is not defined and dropdown list is not opened
		if (!onBlur && !showOptionsList) {
			onCancel?.();
		}
	};

	const multiSelectPopperMemo = useMemo(() => {
		return function multiSelectPopper(props: PopperProps): JSX.Element {
			const { style } = props;

			if (popperFitContentWidth && style.width) {
				style.minWidth = style.width;
				style.width = "fit-content";
				style.maxWidth = POPPER_MAX_WIDTH;
			}

			const PopperComponent = hideNoResults ? StyledPopper : Popper;

			return (
				<PopperComponent
					{...props}
					style={style}
				/>
			);
		};
	}, [popperFitContentWidth]);

	return (
		<StyledAutocomplete
			ListboxComponent={isLargeList
				? React.forwardRef((props: ReactWindowListboxComponentProps, ref: React.ForwardedRef<HTMLDivElement>) => (
					<ReactWindowListboxComponent
						{...props}
						isSlimCard={slimCard}
						largeListRef={ref}
					/>
				))
				: ListboxComponent}
			PopperComponent={multiSelectPopperMemo}
			autoHighlight
			blurOnSelect={false} // this should be false to work properly with renderIntput's inputProps.value preservation
			clearOnBlur={clearOnBlur}
			componentsProps={{
				popupIndicator:{
					"data-csi-automation": `dropdown-Chevron-${dataCsiAutomation}`,
				} as MultiSelectIconButtonProps,
			}}
			data-csi-automation="MultiSelect"
			disableClearable={!selectedValuesExist}
			disableCloseOnSelect
			filterOptions={filterOptions}
			forcePopupIcon
			getOptionDisabled={getComponentItemDisabled}
			getOptionLabel={getOptionLabel}
			groupBy={groupBy}
			isOptionEqualToValue={isComponentItemEqualToValue}
			multiple
			onBlur={_onBlur}
			onChange={onChangeValue}
			onClose={onCloseDropdown}
			onOpen={onOpenDropdown}
			open={showOptions}
			options={opts}
			renderGroup={renderGroup}
			renderInput={renderInput}
			renderOption={renderOption}
			renderTags={renderTags}
			selectActions={(!DropdownPlaceholder || delayedRequestHasResults) && selectActions}
			value={values}
			{...otherProps}
		/>
	);
}

export default MultiSelect;
